import keys from 'constants/keys'
import { POP_REFERENCE_TYPE } from 'constants/enums'
import { IPop } from 'constants/interfaces'

export enum ActionPopResultType {
  NONE = 'NONE',
}

type ActionPopResult = { type: ActionPopResultType.NONE }

export interface ActionPopHandlerContext {
  pop: IPop
  navigate: (path: string) => void
}

interface ActionPopHandler {
  referenceLabel: string
  actionLabel: string
  descriptionLocaleKey: string
  // Static title fallback, used when the pop's own pop_title is empty (DRIVE_FOLDER: the folder name moved
  // to pop_body, so pop_title is sent empty and the title is always this fixed locale key instead).
  titleLocaleKey?: string
  // When true, pop_body is a VALUE to bold-append after the translated descriptionLocaleKey (DRIVE_FOLDER's
  // folder name), instead of free-text overriding the whole description (other reference types' behavior).
  interpolateBodyIntoDescription?: boolean
  execute: (context: ActionPopHandlerContext) => Promise<ActionPopResult>
}

const tasksActionPopHandler: ActionPopHandler = {
  referenceLabel: 'Tasks',
  actionLabel: 'ref_pop_ack',
  descriptionLocaleKey: '',
  execute: async ({ pop, navigate }) => {
    const view = pop.reference_params?.view
    navigate(view !== undefined ? `${keys.ROUTE_NAMES.HUB}/${view}` : keys.ROUTE_NAMES.HUB)
    return { type: ActionPopResultType.NONE }
  },
}

const productPerformanceActionPopHandler: ActionPopHandler = {
  referenceLabel: 'Product Performance',
  actionLabel: 'Go to Product Performance',
  descriptionLocaleKey: 'action_pop_product_performance_description',
  execute: async ({ navigate }) => {
    navigate(keys.ROUTE_NAMES.PRODUCT_PERFORMANCE)
    return { type: ActionPopResultType.NONE }
  },
}

const driveFolderActionPopHandler: ActionPopHandler = {
  referenceLabel: 'Library',
  actionLabel: 'new_content_pop_btn',
  titleLocaleKey: 'new_content_pop_title',
  // pop_body carries the folder name (see interpolateBodyIntoDescription) — the description reads
  // "<new_content_pop_body text> <b>{folder name}</b>".
  descriptionLocaleKey: 'new_content_pop_body',
  interpolateBodyIntoDescription: true,
  execute: async ({ pop, navigate }) => {
    const folderId = pop.reference_params?.folder_id
    navigate(folderId ? keys.ROUTE_NAMES.LIBRARY_FOLDER(folderId) : keys.ROUTE_NAMES.LIBRARY)
    return { type: ActionPopResultType.NONE }
  },
}

const unsupportedActionPopHandler: ActionPopHandler = {
  referenceLabel: 'Action',
  actionLabel: 'Open',
  descriptionLocaleKey: 'action_pop_default_description',
  execute: async ({ pop }) => {
    throw new Error(`Unsupported action pop reference type: ${pop.reference_type}`)
  },
}

const actionPopHandlers: Partial<Record<POP_REFERENCE_TYPE, ActionPopHandler>> = {
  [POP_REFERENCE_TYPE.TASKS]: tasksActionPopHandler,
  [POP_REFERENCE_TYPE.PRODUCT_PERFORMANCE]: productPerformanceActionPopHandler,
  [POP_REFERENCE_TYPE.DRIVE_FOLDER]: driveFolderActionPopHandler,
  // Add future reference_type handlers here.
}

export const getActionPopHandler = (referenceType?: number): ActionPopHandler => {
  return actionPopHandlers[referenceType as POP_REFERENCE_TYPE] || unsupportedActionPopHandler
}
